import _ from "lodash";
/**--external-- */
import React, { useEffect } from "react";
import { graphql, compose } from "lib/apolloClient";
import { connect } from "react-redux";

/**--internal-- */
import { getFilterOptionsQuery } from "../../../modules/Queries";
import { getFilterOptionsFromCache } from "../../../modules/GraphqlHelpers";
import {
  getFilterOptionParam,
  getFilterOptions,
  getFilterDisabilityStatusConfig,
  getFilterParam,
  getValidFilterValuesStatus,
} from "../../../modules/Utils";
import { getProjectObjectivesLabelMapping } from "ProgressReportCommon/utils";
import {
  FilterLabelButton,
  Tooltip,
  DSProvider,
  Dropdown,
  DropdownMenu,
} from "@toddle-edu/ds-web";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { getEntitySubType } from "Utils";

import FilterDropdownV2 from "UIComponents/FilterDropdownV2";
import { useI18n } from "Hooks";
import { getDefaultNameSortPreference } from "modules/CommonSelectors";

import {
  ELEMENT_TYPE_MYP_OBJECTIVES,
  ELEMENT_TYPE_PERSONAL_OBJECTIVES,
  ELEMENT_TYPE_COMMUNITY_OBJECTIVES,
} from "Constants/stringConstants";

const getDisabledStateInfoElement = ({
  label,
  filterDisabilityStatusConfig,
  t,
}) => {
  const { stateCode, parentFilterLabel } = filterDisabilityStatusConfig;

  switch (stateCode) {
    case "LOADING":
      return t("snp:loading_options");
    case "NO_OPTIONS":
      return t("common:no_with_label", {
        label: t("common:option_plural"),
      });
    case "PARENT_FILTER_MULTI_VALUE":
      return t("snp:select_one_parent_option", {
        parent: parentFilterLabel,
        label,
      });
    default:
      return null;
  }
};

const DynamicFilter = props => {
  const {
    options,
    isLoading,
    isData,
    values,
    isCheckList,
    label,
    levels,
    entityType,
    parentFiltersData,
    isParent,
    dependentFilterParents,
    updateLoadingFilters,
    updateInputField,
    updateFilterOptions,
    markDependentFilterAsUpdated,
    parentFilterObj,
    projectNodeDetails,
    buttonType = "outlined",
    testId,
  } = props;

  const totalOptions = _.size(options);

  const isDependent = !_.isEmpty(dependentFilterParents);

  useEffect(() => {
    return () => {
      /**Clear pending filters when component is unmounted */
      updateLoadingFilters({ isReady: true, filter: entityType });
    };
  }, []);

  useEffect(() => {
    if (_.isEmpty(parentFilterObj) || !isDependent) {
      return;
    }

    const dependentFilters = _.get(parentFilterObj, "dependentFilters", []);

    if (!_.includes(dependentFilters, entityType)) {
      return;
    }

    updateInputField({ values: [], key: entityType });

    markDependentFilterAsUpdated({ filter: entityType });
  }, [parentFilterObj]);

  /**update filter options when old data from cache is available,
   * latest data is available.
   * By updating options this way, user can see options early,
   * if they are available in cache.  */
  useEffect(() => {
    if (!isLoading) {
      const { areValuesUpdated, updatedValues } = getValidFilterValuesStatus({
        options,
        values,
        entityType,
        projectNodeUID: _.get(projectNodeDetails, "projectNodeUID", ""),
      });

      if (areValuesUpdated) {
        updateInputField({ values: updatedValues, key: entityType, isParent });
      }
    }

    updateFilterOptions({
      filterOptions: options,
      filter: entityType,
    });

    updateLoadingFilters({ filter: entityType, isReady: isData });
  }, [isLoading]);

  const { t } = useI18n(["snp", "common"]);

  const filterDisabilityStatusConfig = getFilterDisabilityStatusConfig({
    parentFiltersData,
    config: dependentFilterParents,
    totalOptions,
    isLoading,
    levels,
  });

  if (levels === 1) {
    if (!isCheckList) {
      const { label: labelValue = "" } =
        _.find(options, ["value", _.first(values)]) ?? {};
      const optionsForFilter = _.map(options, ({ label, value }) => ({
        label,
        key: value,
      }));
      return (
        <ConditionalWrapper
          type={"wrapperAsElement"}
          condition={filterDisabilityStatusConfig.isDisabled}
          wrapper={
            <Tooltip
              tooltip={getDisabledStateInfoElement({
                label,
                filterDisabilityStatusConfig,
                t,
              })}
            />
          }
        >
          <div>
            <Dropdown
              overlay={
                <DropdownMenu
                  dsVersion="2.0"
                  options={optionsForFilter}
                  value={_.first(values)}
                  onClick={({ key }) =>
                    updateInputField({
                      values: [key],
                      key: entityType,
                      isParent,
                    })
                  }
                />
              }
            >
              <FilterLabelButton
                label={label}
                variant={"neutral"}
                type={buttonType}
                dsVersion="2.0"
                labelValue={labelValue}
                testId={testId}
              />
            </Dropdown>
          </div>
        </ConditionalWrapper>
      );
    }
    return (
      <DSProvider dsVersion="2.0">
        <ConditionalWrapper
          type={"wrapperAsElement"}
          condition={filterDisabilityStatusConfig.isDisabled}
          wrapper={
            <Tooltip
              tooltip={getDisabledStateInfoElement({
                label,
                filterDisabilityStatusConfig,
                t,
              })}
            />
          }
        >
          <div>
            <FilterDropdownV2
              label={label}
              name={"filters"}
              type={"CHECKLIST_DROPDOWN"}
              options={options}
              updateInputField={({ filters }) =>
                updateInputField({
                  values: filters,
                  key: entityType,
                  isParent,
                })
              }
              value={values || []}
              isDisabled={filterDisabilityStatusConfig.isDisabled}
              showSearchBar={true}
              noOptionLabel={"None"}
              filterLabelButtonProps={{
                type: buttonType,
              }}
              showAllOption
              testId={testId}
            />
          </div>
        </ConditionalWrapper>
      </DSProvider>
    );
  }

  const optionsForFilterList = _.map(options, ({ key, label, options }) => {
    return {
      value: key,
      options: _.map(options, ({ isArchived, label, value }) => ({
        isArchived,
        label,
        value: `${key}.${value}`,
      })),
      label,
    };
  });

  const valuesForFilterList = _.concat(
    ..._.map(values, ({ key, values }) => {
      return _.map(values, value => `${key}.${value}`);
    })
  );

  const updateFiltersList = ({ filters }) => {
    const values = {};
    _.forEach(filters, filter => {
      const [key, value] = _.split(filter, ".");
      if (values[key]) {
        values[key] = _.concat(values[key], value);
      } else {
        values[key] = [value];
      }
    });
    updateInputField({
      values: _.map(values, (values, key) => ({ key, values })),
      key: entityType,
      isParent,
    });
  };

  return (
    <DSProvider dsVersion="2.0">
      <ConditionalWrapper
        type={"wrapperAsElement"}
        condition={filterDisabilityStatusConfig.isDisabled}
        wrapper={
          <Tooltip
            tooltip={getDisabledStateInfoElement({
              label,
              filterDisabilityStatusConfig,
              t,
            })}
          />
        }
      >
        <div>
          <FilterDropdownV2
            label={label}
            showAllOption={isCheckList}
            name={"filters"}
            type={"CHECKLIST_DROPDOWN"}
            options={optionsForFilterList}
            updateInputField={updateFiltersList}
            value={valuesForFilterList}
            isDisabled={filterDisabilityStatusConfig.isDisabled}
            showSearchBar={true}
            noOptionLabel={"None"}
            filterLabelButtonProps={{
              type: buttonType,
            }}
            testId={testId}
          />
        </div>
      </ConditionalWrapper>
    </DSProvider>
  );
};

const mapStateToProps = (state, ownProps) => {
  const organizationId = _.get(state, "login.userInfo.org_id", "");

  const {
    entityType,
    parentFiltersData,
    curriculumProgramId,
    parentFilterObj,
    dependentFilterParents,
    curriculumType,
    projectNodeDetails,
    activeTab,
    excludeBlueprintCourses = false,
  } = ownProps;

  const dependentFilters = _.get(parentFilterObj, "dependentFilters", []);

  const currentParentFilters = _.map(
    dependentFilterParents,
    ({ parent }) => parent
  );

  const commonFilters = _.intersection(dependentFilters, currentParentFilters);

  const optionParam = getFilterOptionParam({
    entityType,
    parentFiltersData,
    curriculumProgramId,
    curriculumType,
    excludeBlueprintCourses,
  });

  const filterParam = getFilterParam({ entityType, parentFiltersData });

  let filterParamWithEntitySubType = {};

  if (
    _.includes(
      [
        ELEMENT_TYPE_MYP_OBJECTIVES,
        ELEMENT_TYPE_PERSONAL_OBJECTIVES,
        ELEMENT_TYPE_COMMUNITY_OBJECTIVES,
      ],
      entityType
    )
  ) {
    filterParamWithEntitySubType = getFilterParam({
      entityType,
      parentFiltersData,
      entitySubType: getEntitySubType({
        projectNodeUID: _.get(projectNodeDetails, "projectNodeUID", ""),
      }),
    });
  }

  return {
    id: organizationId,
    optionParam,
    shouldSkipQuery: !_.isEmpty(commonFilters),
    filterOptions: [],
    filterParam,
    isLoading: true,
    isData: false,
    filterParamWithEntitySubType,
    defaultNameSortPreference: getDefaultNameSortPreference(state),
  };
};

export default compose(
  connect(mapStateToProps),
  graphql(getFilterOptionsQuery, {
    name: "getFilterOptionsQuery",
    alias: "getFilterOptionsQuery",
    skip: ({ shouldSkipQuery }) => shouldSkipQuery,
    options: ({
      baseSetType,
      entityType,
      id,
      optionParam,
      isProjectNode,
      filterParam,
      filterParamWithEntitySubType,
    }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          entityType: baseSetType ? baseSetType : entityType,
          id,
          options: optionParam,
          filters: isProjectNode ? filterParamWithEntitySubType : filterParam,
        },
      };
    },
    props: ({
      getFilterOptionsQuery: { variables, networkStatus },
      ownProps: {
        entityType,
        levels,
        attributes,
        defaultNameSortPreference,
        isProjectNode,
        projectNodeDetails,
        t,
      },
    }) => {
      const data = getFilterOptionsFromCache(variables);

      let updatedOptions = [];

      const isCachedDataAvailable = !_.isEmpty(data);

      if (isCachedDataAvailable) {
        updatedOptions = getFilterOptions({
          data,
          levels,
          attributes,
          entityType,
          defaultNameSortPreference,
          projectNodeUID: _.get(projectNodeDetails, "projectNodeUID", ""),
          t,
        });
      }

      if (isProjectNode && entityType === ELEMENT_TYPE_MYP_OBJECTIVES) {
        const projectNodeUID = _.get(projectNodeDetails, "projectNodeUID", "");
        updatedOptions =
          projectNodeUID === "PP_DETAILS"
            ? _.map(updatedOptions, (option, index) => ({
                ...option,
                label: getProjectObjectivesLabelMapping({
                  index,
                  projectType: "PERSONAL",
                }),
              }))
            : projectNodeUID === "CP_DETAILS"
            ? _.map(updatedOptions, (option, index) => ({
                ...option,
                label: getProjectObjectivesLabelMapping({
                  index,
                  projectType: "COMMUNITY",
                }),
              }))
            : updatedOptions;
      }

      return {
        isData: !_.isEmpty(data),
        isLoading: _.includes([1, 2], networkStatus),
        options: updatedOptions,
      };
    },
  })
)(DynamicFilter);
